<template>
  <v-container fluid class="pa-0 landing">
    <!-- HERO -->
    <section class="hero d-flex align-center">
      <div class="hero-glow" />
      <v-container>
        <v-row align="center">
          <v-col cols="12" md="7">
            <div class="text-overline text-primary mb-3 tracking-wide">
              {{ resources.LandingHeroEyebrow || "ENTRENÁ A TU MANERA" }}
            </div>
            <h1 class="hero-title mb-6">
              {{ resources.LandingHeroTitleLine1 || "Reservá, entrená y" }}<br class="d-none d-md-block" />
              {{ resources.LandingHeroTitleLine2 || "superate todos los días." }}
            </h1>

            <p class="text-medium-emphasis hero-subtitle mb-8">
              {{ resources.LandingHeroSubtitle || "Gestioná tus actividades, reservas y membresía desde un solo lugar, con una experiencia simple, rápida y moderna." }}
            </p>

            <div class="d-flex flex-wrap ga-4 mb-10">
              <v-btn
                color="primary"
                size="x-large"
                rounded="xl"
                class="cta-btn"
                href="/Home/Register"
              >
                {{ resources.LandingStartNow || "Empezar ahora" }}
              </v-btn>

              <v-btn
                variant="outlined"
                size="x-large"
                rounded="xl"
                class="cta-btn-outline"
                @click="scrollTo('activities')"
              >
                {{ resources.LandingViewActivities || "Ver actividades" }}
              </v-btn>
            </div>

            <div class="d-flex flex-wrap ga-8" v-if="data">
              <div v-for="stat in heroStats" :key="stat.label">
                <div class="text-h4 font-weight-bold text-primary">
                  {{ stat.value }}
                </div>
                <div class="text-caption text-medium-emphasis">
                  {{ stat.label }}
                </div>
              </div>
            </div>
          </v-col>

          <v-col cols="12" md="5" class="d-none d-md-block">
            <!-- FEAT-118: the hero card had no image, just an icon placeholder.
                 Reuses the same gym photo already used on the login/reset
                 screens (wwwroot/assets/imgs/gym-left.jpeg), so it's real
                 imagery already part of the app's branding, not a stock
                 placeholder. -->
            <div class="hero-visual">
              <v-img
                src="/assets/imgs/gym-left.jpeg"
                cover
                class="hero-visual-img"
              />
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- FEATURES -->
    <v-container class="py-16">
      <div class="text-center mb-12">
        <div class="text-overline text-primary mb-2">{{ resources.LandingFeaturesEyebrow || "POR QUÉ ELEGIRNOS" }}</div>
        <h2 class="section-title">{{ resources.LandingFeaturesTitle || "Todo tu entrenamiento, en un solo lugar" }}</h2>
      </div>

      <v-row>
        <v-col cols="12" md="4" v-for="item in features" :key="item.title">
          <div class="glass-card feature-card pa-8 h-100">
            <div class="feature-icon mb-6">
              <v-icon :icon="item.icon" size="28" />
            </div>
            <h3 class="text-h6 font-weight-bold mb-3">{{ item.title }}</h3>
            <p class="text-medium-emphasis">{{ item.text }}</p>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- ACTIVITIES -->
    <section id="activities" class="section-alt py-16">
      <v-container>
        <div class="text-center mb-12">
          <div class="text-overline text-primary mb-2">{{ resources.LandingActivitiesEyebrow || "NUESTRA OFERTA" }}</div>
          <h2 class="section-title">{{ resources.Activities || "Actividades" }}</h2>
        </div>

        <v-row v-if="loading">
          <v-col cols="12" md="4" v-for="n in 3" :key="n">
            <v-skeleton-loader type="card" class="rounded-xl" />
          </v-col>
        </v-row>

        <v-alert
          v-else-if="!data?.activities?.length"
          type="info"
          variant="tonal"
          class="mx-auto"
          max-width="480"
        >
          {{ resources.LandingNoActivitiesYet || "Muy pronto vamos a publicar nuestras actividades disponibles." }}
        </v-alert>

        <v-row v-else>
          <v-col
            cols="12"
            sm="6"
            md="4"
            v-for="activity in data.activities"
            :key="activity.name"
          >
            <div
              class="glass-card activity-card pa-6 h-100 d-flex align-center ga-4"
            >
              <div class="activity-icon">
                <v-icon :icon="iconFor(activity.name)" size="26" />
              </div>
              <div>
                <h3 class="text-subtitle-1 font-weight-bold mb-1">
                  {{ activity.name }}
                </h3>
                <v-chip
                  v-if="activity.onlyAdults"
                  size="x-small"
                  color="warning"
                  variant="tonal"
                  >+18</v-chip
                >
                <v-chip v-else size="x-small" color="success" variant="tonal"
                  >{{ resources.LandingAllAges || "Todas las edades" }}</v-chip
                >
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- PLANS -->
    <v-container class="py-16" id="plans">
      <div class="text-center mb-12">
        <div class="text-overline text-primary mb-2">{{ resources.LandingPlansEyebrow || "MEMBRESÍAS" }}</div>
        <h2 class="section-title">{{ resources.LandingChooseYourPlan || "Elegí tu plan" }}</h2>
      </div>

      <v-row v-if="loading">
        <v-col cols="12" md="4" v-for="n in 3" :key="n">
          <v-skeleton-loader type="card" class="rounded-xl" />
        </v-col>
      </v-row>

      <v-alert
        v-else-if="!data?.passes?.length"
        type="info"
        variant="tonal"
        class="mx-auto"
        max-width="480"
      >
        {{ resources.LandingNoPlansYet || "Consultá con nosotros por nuestros planes disponibles." }}
      </v-alert>

      <v-row v-else justify="center">
        <v-col
          cols="12"
          sm="6"
          md="4"
          v-for="(pass, i) in data.passes"
          :key="pass.name"
        >
          <div
            class="glass-card plan-card pa-8 h-100 d-flex flex-column"
            :class="{ 'plan-card--featured': i === featuredPlanIndex }"
          >
            <v-chip
              v-if="i === featuredPlanIndex"
              color="primary"
              size="small"
              class="align-self-start mb-4"
            >
              {{ resources.LandingMostChosen || "Más elegido" }}
            </v-chip>
            <h3 class="text-h6 font-weight-bold mb-2">{{ pass.name }}</h3>
            <div class="plan-price mb-1">
              {{ formatCurrency(pass.price) }}
            </div>
            <p class="text-caption text-medium-emphasis mb-6">
              {{
                pass.attendanceAmount > 0
                  ? formatMessage(resources.LandingAccessCount || "{0} accesos", pass.attendanceAmount)
                  : (resources.LandingUnlimitedAccess || "Accesos ilimitados")
              }}
            </p>
            <v-spacer />
            <v-btn
              color="primary"
              variant="flat"
              rounded="lg"
              block
              href="/Home/Register"
            >
              {{ resources.LandingChoosePlan || "Elegir plan" }}
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- LOCATIONS -->
    <section
      class="section-alt py-16"
      id="locations"
      v-if="!loading && data?.branches?.length"
    >
      <v-container>
        <div class="text-center mb-12">
          <div class="text-overline text-primary mb-2">{{ resources.LandingLocationsEyebrow || "DÓNDE ESTAMOS" }}</div>
          <h2 class="section-title">{{ resources.LandingOurBranches || "Nuestras sedes" }}</h2>
        </div>

        <v-row>
          <v-col
            cols="12"
            md="6"
            lg="4"
            v-for="branch in data.branches"
            :key="branch.name"
          >
            <div class="glass-card pa-6 h-100">
              <div class="d-flex align-center ga-3 mb-4">
                <v-icon icon="mdi-map-marker" color="primary" />
                <h3 class="text-subtitle-1 font-weight-bold">
                  {{ branch.name }}
                </h3>
              </div>
              <p class="text-body-2 text-medium-emphasis mb-2">
                {{ branch.address
                }}<span v-if="branch.city">, {{ branch.city }}</span>
              </p>
              <p
                class="text-body-2 text-medium-emphasis mb-2"
                v-if="branch.phone"
              >
                <v-icon icon="mdi-phone" size="16" class="mr-1" />{{
                  branch.phone
                }}
              </p>
              <p class="text-body-2 text-medium-emphasis">
                <v-icon icon="mdi-clock-outline" size="16" class="mr-1" />{{
                  branch.openingTime
                }}
                - {{ branch.closingTime }}
              </p>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- FINAL CTA -->
    <v-container class="py-16">
      <div class="glass-card final-cta pa-10 pa-md-16 text-center">
        <h2 class="section-title mb-4">{{ resources.LandingFinalCtaTitle || "Comenzá hoy mismo" }}</h2>
        <p class="text-h6 font-weight-regular text-medium-emphasis mb-8">
          {{ resources.LandingFinalCtaSubtitle || "Unite y administrá todas tus actividades y reservas desde una sola plataforma." }}
        </p>
        <v-btn
          color="primary"
          rounded="xl"
          size="x-large"
          class="cta-btn"
          href="/Home/Register"
        >
          {{ resources.LandingCreateAccount || "Crear cuenta" }}
        </v-btn>
      </div>
    </v-container>

    <!-- FOOTER -->
    <!-- FEAT-118: no footer existed at all — a visitor had no on-page way to
         find contact info without navigating further. Reuses the same
         data.branches already fetched for the LOCATIONS section above,
         rather than a second request. -->
    <footer
      class="landing-footer py-10"
      v-if="!loading && data?.branches?.length"
    >
      <v-container>
        <v-row>
          <v-col cols="12" md="4" class="mb-6 mb-md-0">
            <div class="d-flex align-center ga-3 mb-3">
              <v-img src="/assets/logo.png" max-width="48" contain></v-img>
              <span class="text-h6 font-weight-bold primary">Gymtonic</span>
            </div>
            <p class="text-body-2 text-medium-emphasis">
              {{ resources.LandingFooterDescription || "Gestioná tus actividades, reservas y membresía desde un solo lugar." }}
            </p>
            <p class="text-body-2 text-medium-emphasis mb-1">
              <v-icon icon="mdi-whatsapp" size="16" class="mr-1" /> +54 9 266 401-2501
            </p>
            <p class="text-body-2 text-medium-emphasis mb-1">
              <v-icon icon="mdi-instagram" size="16" class="mr-1" /> gymtonic7
            </p>
          </v-col>

          <v-col cols="12" md="8">
            <div class="text-overline text-primary mb-3">{{ resources.LandingContactEyebrow || "CONTACTO" }}</div>
            <v-row>
              <v-col
                cols="12"
                sm="6"
                lg="4"
                v-for="branch in data.branches"
                :key="branch.name"
              >
                <div class="text-subtitle-2 font-weight-bold mb-1">
                  {{ branch.name }}
                </div>
                <p class="text-body-2 text-medium-emphasis mb-1">
                  <v-icon icon="mdi-map-marker" size="16" class="mr-1" />{{
                    branch.address
                  }}<span v-if="branch.city">, {{ branch.city }}</span>
                </p>
                <p
                  class="text-body-2 text-medium-emphasis mb-0"
                  v-if="branch.phone"
                >
                  <v-icon icon="mdi-phone" size="16" class="mr-1" />{{
                    branch.phone
                  }}
                </p>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-8" />

        <div class="text-center text-caption text-medium-emphasis">
          &copy; {{ new Date().getFullYear() }} Olympic
        </div>
      </v-container>
    </footer>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { storeToRefs } from "pinia";

const global = window.useGlobalStore();
const { resources } = storeToRefs(global);

const data = ref(null);
const loading = ref(true);

const formatMessage = (template, ...args) =>
  (template || "").replace(/\{(\d+)\}/g, (_, i) => args[i]);

const features = computed(() => [
  {
    icon: "mdi-calendar-check",
    title: resources.value.LandingFeature1Title || "Reservas rápidas",
    text: resources.value.LandingFeature1Text || "Elegí día, horario y actividad en segundos, desde cualquier dispositivo.",
  },
  {
    icon: "mdi-dumbbell",
    title: resources.value.LandingFeature2Title || "Múltiples disciplinas",
    text: resources.value.LandingFeature2Text || "Accedé a musculación, funcional, yoga y más desde una misma cuenta.",
  },
  {
    icon: "mdi-chart-line",
    title: resources.value.LandingFeature3Title || "Seguimiento real",
    text: resources.value.LandingFeature3Text || "Controlá tus asistencias, tu membresía y tu progreso en todo momento.",
  },
]);

const featuredPlanIndex = computed(() => {
  if (!data.value?.passes?.length) return -1;
  return Math.min(1, data.value.passes.length - 1);
});

const heroStats = computed(() => {
  if (!data.value) return [];
  const s = data.value.stats;
  const stats = [];
  if (s.memberCount > 0)
    stats.push({ value: `${s.memberCount}+`, label: resources.value.LandingStatMembers || "Socios" });
  if (s.activityCount > 0)
    stats.push({ value: s.activityCount, label: resources.value.Activities || "Actividades" });
  if (s.branchCount > 0) stats.push({ value: s.branchCount, label: resources.value.LandingStatBranches || "Sedes" });
  if (s.coachCount > 0)
    stats.push({ value: s.coachCount, label: resources.value.LandingStatCoaches || "Profesionales" });
  return stats;
});

const activityIcons = [
  { match: /muscul|fuerza|pesas|gym/i, icon: "mdi-weight-lifter" },
  { match: /funcional|crossfit|hiit/i, icon: "mdi-run-fast" },
  { match: /yoga|pilates|elong|movilidad/i, icon: "mdi-yoga" },
  { match: /spinning|bici|cycl/i, icon: "mdi-bike" },
  { match: /nata|pileta|pool|aqua/i, icon: "mdi-swim" },
  { match: /box|kick/i, icon: "mdi-boxing-glove" },
  { match: /danza|zumba|baile/i, icon: "mdi-music-note" },
];
const iconFor = (name) =>
  activityIcons.find((a) => a.match.test(name))?.icon ?? "mdi-dumbbell";

const formatCurrency = (value) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(value || 0);

const scrollTo = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

onMounted(() => {
  window.axios
    .get("/Home/LandingData")
    .then((res) => {
      data.value = res.data.content;
    })
    .catch(() => {
      data.value = { activities: [], passes: [], branches: [], stats: {} };
    })
    .finally(() => {
      loading.value = false;
    });
});
</script>

<style scoped>
.landing {
  overflow: hidden;
}

.tracking-wide {
  letter-spacing: 0.12em;
}

/* HERO */
.hero {
  min-height: 92vh;
  position: relative;
}

.hero-glow {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(
      60% 50% at 15% 20%,
      rgba(var(--v-theme-primary), 0.16),
      transparent 70%
    ),
    radial-gradient(
      50% 40% at 85% 80%,
      rgba(var(--v-theme-primary), 0.08),
      transparent 70%
    );
  pointer-events: none;
}

.hero-title {
  font-size: clamp(2.25rem, 5vw, 3.75rem);
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: -0.02em;
}

.hero-subtitle {
  font-size: 1.15rem;
  max-width: 520px;
  line-height: 1.6;
}

.cta-btn {
  font-weight: 700;
  letter-spacing: 0.02em;
  box-shadow: 0 12px 32px -8px rgba(var(--v-theme-primary), 0.55);
}

.cta-btn-outline {
  font-weight: 600;
  border-color: rgba(var(--v-theme-on-surface), 0.24) !important;
}

.hero-visual {
  aspect-ratio: 4 / 5;
  border-radius: 32px;
  overflow: hidden;
  border: 1px solid rgba(var(--v-theme-primary), 0.25);
  box-shadow: 0 30px 80px -20px rgba(0, 0, 0, 0.5);
}

.hero-visual-img {
  height: 100%;
  width: 100%;
}

/* SECTION HEADINGS */
.section-title {
  font-size: clamp(1.75rem, 3vw, 2.5rem);
  font-weight: 800;
  letter-spacing: -0.01em;
}

.section-alt {
  background: linear-gradient(
    180deg,
    transparent,
    rgba(var(--v-theme-surface), 0.35) 15%,
    rgba(var(--v-theme-surface), 0.35) 85%,
    transparent
  );
}

/* GLASS CARDS */
.glass-card {
  background: rgba(var(--v-theme-surface), 0.55);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 24px;
  backdrop-filter: blur(12px);
  transition:
    transform 0.25s ease,
    border-color 0.25s ease,
    box-shadow 0.25s ease;
}

.glass-card:hover {
  transform: translateY(-4px);
  border-color: rgba(var(--v-theme-primary), 0.35);
  box-shadow: 0 20px 48px -16px rgba(0, 0, 0, 0.45);
}

.feature-icon,
.activity-icon {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(var(--v-theme-primary), 0.14);
  color: rgb(var(--v-theme-primary));
}

/* PLANS */
.plan-card {
  border-radius: 28px;
}

.plan-card--featured {
  border-color: rgba(var(--v-theme-primary), 0.55);
  box-shadow: 0 24px 60px -20px rgba(var(--v-theme-primary), 0.35);
}

.plan-price {
  font-size: 2.25rem;
  font-weight: 800;
  color: rgb(var(--v-theme-primary));
}

/* FINAL CTA */
.final-cta {
  border-radius: 32px;
  background: linear-gradient(
    135deg,
    rgba(var(--v-theme-primary), 0.14),
    rgba(var(--v-theme-surface), 0.7)
  );
}

/* FOOTER */
.landing-footer {
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  background: rgba(var(--v-theme-surface), 0.35);
}
</style>
